import React from "react";
import ReactMarkdown from "react-markdown";
import SendIcon from "@mui/icons-material/Send";
import CircularProgress from "@mui/icons-material/RotateRight";
import AssistantIcon from "@mui/icons-material/Assistant";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";

import { streamAIReportingAPI } from "../requests/utilities/streamingRequests";
import { API_form_survey_chat_stream } from "../../../constants";

// Mirrors SURVEY_CHAT_MAX_QUERY_CHARS on the backend.
const MAX_QUESTION_CHARS = 2000;

const SUGGESTIONS = [
  "Summarize this survey",
  "What are the main themes in the open answers?",
  "Where are scores lowest, and what should we look into?",
  "What is going well?",
];

const md = {
  p: ({ node, ...props }) => <p className="mb-1 leading-relaxed" {...props} />,
  strong: ({ node, ...props }) => <strong className="font-semibold" {...props} />,
  ul: ({ node, ...props }) => <ul className="list-disc ml-5 mb-1 space-y-1" {...props} />,
  ol: ({ node, ...props }) => <ol className="list-decimal ml-5 mb-1 space-y-1" {...props} />,
  li: ({ node, ...props }) => <li className="leading-relaxed" {...props} />,
  h1: ({ node, ...props }) => <p className="font-semibold mt-2 mb-1" {...props} />,
  h2: ({ node, ...props }) => <p className="font-semibold mt-2 mb-1" {...props} />,
  h3: ({ node, ...props }) => <p className="font-semibold mt-2 mb-1" {...props} />,
};

// Chat with this survey: the shared chat layout (Chat with reports / Company
// Files / logbook), scoped to the report slice on screen. The backend rebuilds
// the same link + answer filters every turn; history is held here.
export default function FormSurveyChatPanel({
  companyId,
  formUuid,
  shareUuid,
  filterState,
  filters,
  sliceLabel,
  onBack,
  onClose,
}) {
  const [question, setQuestion] = React.useState("");
  const [status, setStatus] = React.useState("idle"); // idle | loading
  const [exchanges, setExchanges] = React.useState([]); // {question, askedAt, text, answeredAt}
  const [thinking, setThinking] = React.useState("");
  const messagesEndRef = React.useRef(null);
  const textareaRef = React.useRef(null);
  const requestSeq = React.useRef(0);

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [exchanges, status]);

  React.useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      // Cap at ~5 lines; beyond that the box scrolls instead of growing into
      // the fixed-height modal.
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [question]);

  const resetConversation = () => {
    requestSeq.current += 1;
    setExchanges([]);
    setQuestion("");
    setStatus("idle");
    setThinking("");
  };

  // Answers are scoped to the slice on screen. Changing a link or answer
  // filter starts a fresh conversation and orphans any in-flight stream, so an
  // old-slice answer can never land under the new slice's label (same rule as
  // the logbook chat on a store switch).
  const scopeKey = `${shareUuid || ""}|${JSON.stringify(filterState || [])}`;
  const scopeRef = React.useRef(scopeKey);
  React.useEffect(() => {
    if (scopeRef.current === scopeKey) return;
    scopeRef.current = scopeKey;
    requestSeq.current += 1;
    setExchanges([]);
    setStatus("idle");
    setThinking("");
  }, [scopeKey]);

  const ask = async (text) => {
    const q = (text ?? question).trim();
    if (!q || status === "loading") return;
    const seq = ++requestSeq.current;
    // Interrupted answers are partial: they never go back as context.
    const history = exchanges
      .filter((ex) => ex.text && !ex.interrupted)
      .slice(-5)
      .flatMap((ex) => [
        { role: "user", content: ex.question },
        { role: "assistant", content: ex.text },
      ]);
    setExchanges((prev) => [...prev, { question: q, askedAt: new Date(), text: "", answeredAt: null }]);
    setQuestion("");
    setStatus("loading");
    setThinking("Reading the survey results...");
    const patchLast = (fn) =>
      setExchanges((prev) => {
        if (!prev.length) return prev;
        const next = prev.slice();
        next[next.length - 1] = fn(next[next.length - 1]);
        return next;
      });
    try {
      await streamAIReportingAPI(
        API_form_survey_chat_stream,
        {
          company_id: companyId,
          learning_form_uuid: formUuid,
          share_uuid: shareUuid || undefined,
          filters: filterState && filterState.length ? filterState : undefined,
          user_query: q,
          conversation_history: history.length ? history : undefined,
        },
        {
          onThinking: (t) => seq === requestSeq.current && setThinking(t),
          onToken: (t) => {
            if (seq !== requestSeq.current) return;
            setThinking("");
            patchLast((ex) => ({ ...ex, text: ex.text + t }));
          },
          onDone: () => {},
          onError: () => {},
        }
      );
      if (seq === requestSeq.current) patchLast((ex) => ({ ...ex, answeredAt: new Date() }));
    } catch (e) {
      if (seq === requestSeq.current) {
        patchLast((ex) => ({
          ...ex,
          text: ex.text || "Couldn't analyze the survey right now. Please try again.",
          // Every failed exchange is interrupted, whether it streamed partial
          // text or nothing: neither may go back to the model as an answer.
          interrupted: true,
          partial: Boolean(ex.text),
          answeredAt: new Date(),
        }));
      }
    } finally {
      if (seq === requestSeq.current) {
        setStatus("idle");
        setThinking("");
      }
    }
  };

  const formatTimestamp = (timestamp) =>
    timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  const canSend = question.trim() && status !== "loading";
  const last = exchanges[exchanges.length - 1];
  const streaming = status === "loading" && last && last.text;

  return (
    <div className="flex h-full bg-gray-50 rounded-2xl overflow-hidden">
      <div className="flex-1 flex flex-col min-h-0">
        {/* Header */}
        <div className="bg-white border-b px-4 py-3 flex items-center justify-between shadow-sm shrink-0">
          <div className="flex items-center min-w-0">
            {onBack && (
              <>
                <button
                  onClick={onBack}
                  title="Back to Report"
                  className="flex items-center gap-1.5 -ml-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium text-gray-500 hover:text-violet-700 hover:bg-gray-100 transition-colors"
                >
                  <ArrowBackIcon style={{ fontSize: 18 }} />
                  <span>Report</span>
                </button>
                <div className="h-6 w-px bg-gray-200 mx-3 flex-shrink-0" />
              </>
            )}
            <div className="w-9 h-9 rounded-full bg-violet-600 flex items-center justify-center flex-shrink-0">
              <AssistantIcon fontSize="small" className="text-white" />
            </div>
            <div className="ml-3 min-w-0">
              <h2 className="font-medium text-gray-800">Chat with this Survey</h2>
              <p className="text-xs text-gray-500 truncate">Answers are based on {sliceLabel}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={resetConversation} className="text-sm text-gray-500 hover:text-violet-600 px-2">
              Reset
            </button>
            {onClose && (
              <button
                className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500"
                onClick={onClose}
              >
                <CloseIcon />
              </button>
            )}
          </div>
        </div>

        {/* Filters: the same groups as the report, scoping every answer. */}
        {filters && (
          <div className="shrink-0 max-h-[40vh] overflow-y-auto bg-white border-b border-gray-200 px-4 py-3">
            {filters}
          </div>
        )}

        {/* Messages area */}
        <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
          <div className="mb-4 flex justify-start">
            <div
              className="rounded-lg px-4 py-3 bg-white border border-gray-200 text-gray-700 text-sm"
              style={{ maxWidth: "75%" }}
            >
              <div className="whitespace-pre-line">
                Hi! Ask me anything about this survey&apos;s results. I answer only from the
                responses in the report, and I&apos;ll cite the counts I use. Change the filters
                above to ask about a different group.
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => ask(s)}
                    disabled={status === "loading"}
                    className="px-3 py-1.5 rounded-full border border-gray-300 text-xs font-medium text-gray-600 hover:border-violet-700 hover:text-violet-700 disabled:opacity-50 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {exchanges.map((exchange, index) => (
            <React.Fragment key={index}>
              <div className="mb-4 flex justify-end">
                <div className="rounded-lg px-4 py-3 bg-violet-100 text-gray-700" style={{ maxWidth: "75%" }}>
                  <div className="flex flex-col">
                    <div className="mb-1 whitespace-pre-line">{exchange.question}</div>
                    <span className="text-xs self-end mt-1 text-gray-500">
                      {formatTimestamp(exchange.askedAt)}
                    </span>
                  </div>
                </div>
              </div>
              {exchange.text && (
                <div className="mb-4 flex justify-start">
                  <div
                    className="rounded-lg px-4 py-3 bg-white border border-gray-200 text-gray-700"
                    style={{ maxWidth: "75%" }}
                  >
                    <div className="flex flex-col">
                      <div className="mb-1 text-sm">
                        <ReactMarkdown components={md}>{exchange.text}</ReactMarkdown>
                      </div>
                      {exchange.interrupted && exchange.partial && (
                        <div className="mt-2 pt-2 border-t border-gray-100 text-xs text-amber-700">
                          This answer was cut off. Please ask again.
                        </div>
                      )}
                      {exchange.answeredAt && (
                        <span className="text-xs self-end mt-1 text-gray-400">
                          {formatTimestamp(exchange.answeredAt)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}

          {status === "loading" && !streaming && (
            <div className="flex items-center mb-4">
              <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 flex items-center">
                <CircularProgress fontSize="small" className="text-violet-600 animate-spin mr-2" />
                <span className="text-gray-500 text-sm">{thinking || "Thinking..."}</span>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div className="bg-white border-t p-4 shrink-0">
          <div className="flex">
            <div className="flex-1 bg-gray-100 rounded-full flex items-center px-4">
              <textarea
                ref={textareaRef}
                value={question}
                maxLength={MAX_QUESTION_CHARS}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    ask();
                  }
                }}
                placeholder='Ask about this survey, e.g. "what do people say about leadership?"'
                className="w-full bg-transparent py-2 outline-none resize-none text-gray-700 max-h-[120px] overflow-y-auto"
                rows="1"
              />
              <button
                onClick={() => ask()}
                disabled={!canSend}
                className={`ml-2 rounded-full w-8 h-8 flex items-center justify-center ${
                  canSend ? "bg-violet-600 text-white" : "bg-gray-100 text-gray-500"
                }`}
              >
                <SendIcon fontSize="small" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
